/**
 * 이미지 프록시 URL 변환 유틸리티
 * Worker / SSR / Client 모든 환경에서 안전하게 사용 가능
 */

const URL_BASE = 'http://localhost';
const NGINX_URL = process.env.NEXT_PUBLIC_NGINX_URL ?? '';
const IS_DEV = process.env.NODE_ENV === 'development';

function withParams(
  path: string,
  params: Record<string, string | number | undefined>,
) {
  const u = new URL(path, URL_BASE);
  Object.entries(params).forEach(
    ([k, v]) => v != null && u.searchParams.set(k, String(v)),
  );
  return u.pathname + (u.search || '');
}

/**
 * 경로 세그먼트를 개별적으로 인코딩하여 프록시 URL 생성
 * base: '/api/image', rawPath: '/product/123/abc' → '/api/image/product/123/abc'
 */
function joinEncoded(base: string, rawPath: string) {
  const clean = rawPath.replace(/^\/+/, '');
  const encoded = clean.split('/').map(encodeURIComponent).join('/');
  return `${base}/${encoded}`;
}

/**
 * files 경로에서 파일 key 추출
 * - '.../files/product/123/abc.jpg' → ['product', '123', 'abc.jpg']
 * - '/api/image/123/abc.jpg' → ['123', 'abc.jpg']
 */
function extractFileKey(src: string): string[] | null {
  if (!src) return null;

  let path = '';
  if (src.includes('files')) {
    path = src.split('files')[1] ?? '';
  } else if (src.startsWith('/api/image/')) {
    path = src.split('?')[0].replace(/^\/api\/image/, '');
  } else if (src.startsWith('/api/download/')) {
    path = src.split('?')[0].replace(/^\/api\/download/, '');
  } else if (src.startsWith('/api/video/')) {
    path = src.split('?')[0].replace(/^\/api\/video/, '');
  } else {
    return null;
  }

  const segments = path.replace(/^\/+/, '').split('/').filter(Boolean);
  return segments.length > 0 ? segments : null;
}

/**
 * public 폴더의 정적 파일 경로인지 확인
 * - '/'로 시작하고 'files'를 포함하지 않으며 외부 URL이 아닌 경우
 * - 예: /canvas/dimmed.png, /images/logo.svg
 */
export function isPublicStaticPath(src: string): boolean {
  return (
    src.startsWith('/') &&
    !src.startsWith('/api/') &&
    !src.includes('files') &&
    !/^https?:\/\//i.test(src)
  );
}

/**
 * CDN 정적 파일 경로인지 확인
 * - '/commerce/static'로 시작하는 경로
 * - 예: /commerce/static/template_basic.jpg
 */
export function isCDNPath(src: string): boolean {
  return src.startsWith('/commerce/static') && !/^https?:\/\//i.test(src);
}

export interface ImageProxyUrlProps {
  src: string;
  thumbnailSize?: 'small' | 'medium' | 'large';
}

/**
 * 이미지 URL을 프록시 URL로 변환
 * - public 폴더 경로 → 그대로 반환 (프록시 우회)
 * - 전체 URL (https://...) → /api/image?url=...
 * - files 포함 URL → 경로 추출 후 /api/image/[path]
 */
export function getImageProxyUrl({
  src,
  thumbnailSize,
}: ImageProxyUrlProps): string {
  if (!src) return '';

  // 로컬에서 공용 에셋 참조 시 DEV cloudfront 프록시 사용
  if (IS_DEV && isCDNPath(src)) {
    return `${NGINX_URL}${src}`;
  }

  if (isPublicStaticPath(src)) {
    return src;
  }

  const size = thumbnailSize;

  if (src.startsWith('/api/image')) {
    return withParams(src, { size });
  }

  const key = extractFileKey(src);
  if (key) {
    const path = key.join('/');
    return withParams(joinEncoded('/api/image', path), { size });
  }

  // 외부 http(s) URL → /api/image?url=... 형태로 프록시 통과 시도.
  // 주의: 현재 `/api/image` 라우트는 path-style (`/api/image/product/...`, `/api/image/banner/...`) 만 구현돼 있어
  // `?url=` 쿼리 형태는 환경에 따라 404가 날 수 있다 (특히 rc 망).
  // raw URL을 그대로 써야 하는 도메인별 케이스(예: VTON 결과의 S3/baobab presigned URL)는
  // 호출부에서 전용 helper로 우회할 것 — [[vton-image-url]] 참고.
  if (/^https?:\/\//i.test(src)) {
    return withParams(`/api/image`, { url: src, size });
  }

  return src;
}

/**
 * 다운로드용 프록시 URL 생성 (getImageProxyUrl과 동일한 경로 추출 로직 사용)
 * -  ../files/[...key] → /api/download/[...key]
 * - /api/image/[...key] → /api/download/[...key]
 */
export function getDownloadProxyUrl(src: string): string | null {
  const key = extractFileKey(src);
  if (!key) return null;

  const encoded = key.map(encodeURIComponent).join('/');
  return `/api/download/${encoded}`;
}

export function getVideoProxyUrl(src: string): string | null {
  // 로컬에서 공용 에셋 참조 시 DEV cloudfront 프록시 사용
  if (IS_DEV && isCDNPath(src)) {
    return `${NGINX_URL}${src}`;
  }

  if (isPublicStaticPath(src)) {
    return src;
  }

  const key = extractFileKey(src);
  if (!key) return null;

  const encoded = key.map(encodeURIComponent).join('/');
  return `/api/video/${encoded}`;
}
